import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const WordPressAutoSyncManager = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState({
    enabled: false,
    frequency: 'daily',
    check_hour: 6
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [nextSync, setNextSync] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/api/wordpress/auto-sync-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setConfig({
        enabled: response.data.enabled,
        frequency: response.data.frequency,
        check_hour: response.data.check_hour
      });
      
      setLastSync(response.data.last_check);
      setNextSync(response.data.next_check);
    } catch (error) {
      console.error('Error fetching auto-sync config:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/api/wordpress/auto-sync-config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert(`✅ ${response.data.message}`);
      setNextSync(response.data.next_check);
      fetchConfig(); // Refresh to get updated data
    } catch (error) {
      console.error('Error saving auto-sync config:', error);
      alert('❌ Error saving auto-sync configuration');
    } finally {
      setSaving(false);
    }
  };

  const testSync = async () => {
    try {
      const response = await axios.post(`${API}/api/wordpress/check-and-sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert(`✅ ${response.data.message}`);
      fetchConfig(); // Refresh status
    } catch (error) {
      console.error('Error testing sync:', error);
      alert('❌ Error running test sync');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-lg border p-6">
        <h2 className="text-xl font-semibold mb-4">📰 WordPress Auto-Sync Configuration</h2>
        
        {/* Status Display */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium">Status:</span>
              <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                config.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {config.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div>
              <span className="font-medium">Frequency:</span>
              <span className="ml-2 capitalize">{config.frequency}</span>
            </div>
            <div>
              <span className="font-medium">Check Hour:</span>
              <span className="ml-2">{config.check_hour}:00 UTC</span>
            </div>
          </div>
          
          {lastSync && (
            <div className="mt-2 text-sm text-gray-600">
              <strong>Last sync:</strong> {new Date(lastSync).toLocaleString()}
            </div>
          )}
          
          {nextSync && config.enabled && (
            <div className="mt-1 text-sm text-gray-600">
              <strong>Next sync:</strong> {new Date(nextSync).toLocaleString()}
            </div>
          )}
        </div>

        {/* Configuration Form */}
        <div className="space-y-4">
          <div>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => setConfig({...config, enabled: e.target.checked})}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="font-medium">Enable Auto-Sync</span>
            </label>
            <p className="text-sm text-gray-600 mt-1">
              Automatically check for new WordPress posts and import them
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sync Frequency
              </label>
              <select
                value={config.frequency}
                onChange={(e) => setConfig({...config, frequency: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                disabled={!config.enabled}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="manual">Manual Only</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Check Hour (UTC)
              </label>
              <input
                type="number"
                min="0"
                max="23"
                value={config.check_hour}
                onChange={(e) => setConfig({...config, check_hour: parseInt(e.target.value)})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                disabled={!config.enabled}
              />
              <p className="text-xs text-gray-500 mt-1">
                Hour of day to check for new posts (0-23)
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={saveConfig}
              disabled={saving}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
            
            <button
              onClick={testSync}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Test Sync Now
            </button>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
        <h3 className="font-semibold text-blue-800 mb-2">How Auto-Sync Works</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Daily:</strong> Checks for new posts every day at the specified hour</li>
          <li>• <strong>Weekly:</strong> Checks once per week at the specified hour</li>
          <li>• <strong>Manual:</strong> Only syncs when you trigger it manually</li>
          <li>• <strong>New posts only:</strong> Skips existing posts to avoid duplicates</li>
          <li>• <strong>Enhanced formatting:</strong> Preserves bold, italic, and heading styles</li>
          <li>• <strong>Video detection:</strong> Automatically detects embedded YouTube videos</li>
        </ul>
      </div>
    </div>
  );
};

export default WordPressAutoSyncManager;